import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { esDgsfpReal, isNewPlanTransfer } from '../../types/individualDashboard';
import { fmtEur } from '../formatArcaNumbers';
import { puedeFirmarBoletin } from '../puedeFirmarBoletin';
import { isPlatformTransfer } from '../resolvePrimaryTransfer';
import { nombreCortoDeGestora, recortar } from './nombreCortoDeGestora';

/**
 * La lógica de las tarjetas de traspaso, sin pintar nada. Vivía dentro de las tarjetas locales de
 * `MiBeneficioV2/` (`TransferStatusCard`, `VerifyIdentityCard`…); las tarjetas pasaron a ser las
 * de Proa (`ui/transfer-status-card`, `ui/card-action`) y lo que decidían ellas se quedó aquí,
 * igual que estaba (chirigota#586).
 */

/** Los seis estados que cuenta la tarjeta: los cinco del traspaso y `pending`, «te espera a ti». */
export type VarianteTraspaso =
	| 'requested'
	| 'pending'
	| 'inProgress'
	| 'completed'
	| 'rejected'
	| 'cancelled';

/**
 * Estados en los que un traspaso sigue VIVO, los de comparsa (`LIVE_TRANSFER_STATUSES`). Los
 * cerrados y los completados viven en Movimientos.
 */
export const ESTADOS_VIVOS = ['draft', 'requested', 'in_progress'];

/**
 * 🔴 Manda el estado DEL TRASPASO, no el del usuario.
 *
 * Hasta el 29/09/26 esto era un ternario de dos salidas: `completed`, o si no `inProgress`. Pero
 * `TransferStatus` tiene SEIS valores, así que un traspaso `rejected` o `cancelled` —cerrado a
 * mano, como se cierran— se anunciaba como «Traspaso en curso · Lo estamos tramitando con la
 * gestora». Y `requested` decía lo mismo, cuando todavía no ha salido nada hacia la gestora.
 *
 * `kycDone` solo desempata entre «esperándote a ti» y «en curso», y solo mientras el traspaso
 * sigue vivo. Es un estado del USUARIO —`/kyc/status` no lleva plan—, así que no puede pisar lo
 * que le pasó a una fila concreta del histórico: sin esta precedencia, a quien reabría el KYC por
 * un segundo plan se le marcaban como pendientes traspasos cerrados hacía un año.
 */
export function varianteDeTraspaso(
	transfer: PensionPlanTransfer,
	kycDone: boolean
): VarianteTraspaso {
	if (transfer.status === 'completed') return 'completed';
	if (transfer.status === 'rejected') return 'rejected';
	if (transfer.status === 'cancelled') return 'cancelled';
	if (!kycDone) return 'pending';
	return transfer.status === 'in_progress' ? 'inProgress' : 'requested';
}

export interface OpcionesTarjetaTraspaso {
	/** La identidad va con el plan: decide el copy de la tarjeta, no el estado del traspaso. */
	kycDone: boolean;
	/**
	 * Por qué está pendiente `pending`: por defecto la identidad (KYC), el caso de todo traspaso
	 * personal. Un traspaso a un plan de EMPLEO nunca pide KYC, solo firmar la documentación, así
	 * que con `'signature'` el badge y el subtítulo hablan de firma.
	 */
	pendingReason?: 'identity' | 'signature';
	/**
	 * Por defecto «desde» (el origen es de fuera). Para consolidar un plan de empleo anterior en el
	 * actual, el dato relevante sería el destino.
	 */
	originPreposition?: string;
}

/** Lo que pinta `ui/transfer-status-card`, ya traducido. */
export interface TarjetaTraspasoProps {
	variant: VarianteTraspaso;
	title: string;
	subtitle: string;
	badge: string;
	amount?: string;
	originName?: string;
	fromLabel: string;
}

/* Un mapa y no ternarios anidados: con seis variantes, encadenarlos hacía ilegible cuál era el
   copy de cada estado, que es justo lo que hay que poder auditar de un vistazo. */
const COPY: Record<VarianteTraspaso, { titulo: string; subtitulo: string; badge: string }> = {
	requested: { titulo: 'requestedTitle', subtitulo: 'requestedSubtitle', badge: 'requestedBadge' },
	pending: { titulo: 'pendingTitle', subtitulo: 'pendingSubtitle', badge: 'pendingBadge' },
	inProgress: {
		titulo: 'inProgressTitle',
		subtitulo: 'inProgressSubtitle',
		badge: 'inProgressBadge'
	},
	completed: { titulo: 'completedTitle', subtitulo: 'completedSubtitle', badge: 'completedBadge' },
	rejected: { titulo: 'rejectedTitle', subtitulo: 'rejectedSubtitle', badge: 'rejectedBadge' },
	cancelled: { titulo: 'cancelledTitle', subtitulo: 'cancelledSubtitle', badge: 'cancelledBadge' }
};

/* «Nuevo plan» solo cambia el TÍTULO, y solo donde existe la variante: no hay un «Activación
   rechazada» distinto de «Rechazado». */
const CON_TITULO_DE_NUEVO_PLAN: VarianteTraspaso[] = [
	'requested',
	'pending',
	'inProgress',
	'completed'
];

const BASE = 'individual.process.transferCard.';

/**
 * De dónde viene el dinero, en lo que cabe en una línea. `originPayload.entity.name` solo lo
 * traen los traspasos pedidos desde la plataforma (1 de 270 vivos el 23/10/2026); los del funnel
 * llevan `catalog_id`, el mismo que ya usa el detalle del plan (`TransferInfoCard`). Sin nombre,
 * el código DGSFP («N0769») sigue diciendo algo; sin nada, la tarjeta no pinta «desde».
 */
function origenDelTraspaso(transfer: PensionPlanTransfer): string | undefined {
	const nombre =
		nombreCortoDeGestora(transfer.catalog?.entityName) ||
		// El de la plataforma ya viene limpio («MyInvestor (Andbank)»): solo que quepa.
		recortar(((transfer.originPayload?.entity?.name as string | undefined) ?? '').trim());
	if (nombre) return nombre;
	const codigoDelPayload = transfer.originPayload?.dgsfpCode as string | undefined;
	if (esDgsfpReal(transfer.DGSFP_code)) return transfer.DGSFP_code;
	return esDgsfpReal(codigoDelPayload) ? codigoDelPayload : undefined;
}

/**
 * 🔴 «Nuevo plan» no es un traspaso: no hay nada que traspasar, así que ni el importe ni el
 * origen se muestran, y el título habla del PLAN (como ya hace `ProcessTimeline` con «Plan
 * activado»).
 */
export function transferToCardProps(
	transfer: PensionPlanTransfer,
	{ kycDone, pendingReason = 'identity', originPreposition }: OpcionesTarjetaTraspaso,
	translate: (clave: string) => string
): TarjetaTraspasoProps {
	const variant = varianteDeTraspaso(transfer, kycDone);
	const isNewPlan = isNewPlanTransfer(transfer);
	const firma = variant === 'pending' && pendingReason === 'signature';
	const copy = COPY[variant];
	const titulo =
		isNewPlan && CON_TITULO_DE_NUEVO_PLAN.includes(variant) ? `${copy.titulo}NewPlan` : copy.titulo;
	const originName = isNewPlan ? undefined : origenDelTraspaso(transfer);

	return {
		variant,
		title: translate(`${BASE}${titulo}`),
		subtitle: translate(`${BASE}${firma ? 'pendingSubtitleSignature' : copy.subtitulo}`),
		badge: translate(`${BASE}${firma ? 'pendingBadgeSignature' : copy.badge}`),
		// 🔴 Un importe a 0 es que aún no se sabe (el funnel lo pide sin importe): nunca «0,00 €».
		amount:
			!isNewPlan && transfer.amount && parseFloat(transfer.amount) > 0
				? fmtEur(parseFloat(transfer.amount))
				: undefined,
		originName: originName || undefined,
		fromLabel: originPreposition ?? (translate(`${BASE}from`) || 'desde')
	};
}

/**
 * El completado se ve UNA sola vez: es una confirmación puntual («el banner permanente
 * desaparece; en su lugar, una confirmación que se ve una vez»). El aviso persistente es el email
 * transaccional y el detalle sigue en Movimientos. `alwaysShowConfirmed` (panel de depuración y
 * listas que lo enseñan siempre) se salta el gate.
 */
export function ocultarConfirmado(
	variant: VarianteTraspaso,
	yaVisto: boolean,
	alwaysShowConfirmed: boolean
): boolean {
	return variant === 'completed' && yaVisto && !alwaysShowConfirmed;
}

/**
 * Los traspasos de UN plan de empleo, todavía vivos. `my-transfers` no acepta modo y devuelve
 * todos los del usuario, así que se acota por `userEppConfigId` —el plan al que pertenece el
 * traspaso—. Un traspaso de plan individual lo trae a `null` y se queda fuera.
 */
export function traspasosVivosDelPlan(
	transfers: PensionPlanTransfer[],
	userEppConfigId: string | null | undefined
): PensionPlanTransfer[] {
	if (!userEppConfigId) return [];
	return transfers.filter(
		(t) => t.userEppConfigId === userEppConfigId && ESTADOS_VIVOS.includes(t.status ?? '')
	);
}

/** Estado de la tarjeta de identidad: con acción solo `pendiente`. */
export type EstadoIdentidad = 'pendiente' | 'enRevision' | 'rechazado';

export type AccionPendiente =
	| { tipo: 'identidad'; estado: EstadoIdentidad }
	| { tipo: 'destino' }
	| { tipo: 'firma'; transferId: string };

export type TipoAccionPendiente = AccionPendiente['tipo'];

/**
 * 🔴 Los dos estados de comparsa en los que el usuario no puede hacer nada
 * (`KYC_ACTIONABLE_STATUSES` deja fuera estos): la tarjeta sale, pero SIN botón. El botón hacía
 * daño de verdad: `startKyc` no comprueba el estado, devuelve la URL igual y emite `kyc-start`,
 * así que cada clic era un arranque falso en el embudo B2C. Esta lista y la de comparsa tienen
 * que decir lo mismo.
 */
const ESTADOS_KYC_SIN_ACCION = ['PENDING_REVIEW', 'REJECTED'];

/**
 * Cómo está la identidad de la persona, o `null` si ya está verificada (`APPROVED` o
 * `NOT_REQUIRED`). En revisión y rechazada no hay nada que pueda hacer; cualquier otro estado
 * (`PENDING`, `IN_PROGRESS`, `NEEDS_INFO`…) pide verificarse, con botón. Lo comparten Inicio y el
 * Resumen multicuenta (chirigota#585).
 */
export function estadoDeIdentidad(kycStatus: string | null | undefined): EstadoIdentidad | null {
	if (kycStatus === 'APPROVED' || kycStatus === 'NOT_REQUIRED') return null;
	if (ESTADOS_KYC_SIN_ACCION.includes(kycStatus ?? '')) {
		return kycStatus === 'REJECTED' ? 'rechazado' : 'enRevision';
	}
	return 'pendiente';
}

export interface EntradaAccionPendiente {
	/** El traspaso vivo del plan (`draft | requested | in_progress`), o null. */
	traspasoVivo: PensionPlanTransfer | null;
	kycStatus: string | null | undefined;
	/**
	 * 🔴 «Aún no lo sé» NO es «no hace falta»: mientras `/kyc/status` no ha respondido no se afirma
	 * nada. Con el estado por defecto (`NOT_REQUIRED`) a quien tenía el KYC en `PENDING` se le
	 * pedía FIRMAR, y firmar es imposible sin `APPROVED`.
	 */
	kycResuelto: boolean;
	/** Boletines ya firmados: la tarjeta de firma solo tiene sentido si aún no ha firmado. */
	signedBulletinIds: string[];
}

/**
 * LA ACCIÓN QUE BLOQUEA EL TRASPASO, si la hay. Solo UNA a la vez, y en el orden real del trámite:
 * sin identidad no se puede firmar, y sin destino elegido el boletín saldría en blanco (el backend
 * rechaza la firma).
 */
export function accionPendienteDelTraspaso({
	traspasoVivo,
	kycStatus,
	kycResuelto,
	signedBulletinIds
}: EntradaAccionPendiente): AccionPendiente | null {
	if (!traspasoVivo || !kycResuelto) return null;
	const estado = estadoDeIdentidad(kycStatus);
	if (estado) return { tipo: 'identidad', estado };
	if (isPlatformTransfer(traspasoVivo) && !traspasoVivo.selectedArcaPlanId) {
		return { tipo: 'destino' };
	}
	// Las cinco puertas de comparsa viven en `puedeFirmarBoletin`, compartidas con el paso
	// «Firmar» del ProcessTimeline.
	if (puedeFirmarBoletin(traspasoVivo, kycStatus) && !signedBulletinIds.includes(traspasoVivo.id)) {
		return { tipo: 'firma', transferId: traspasoVivo.id };
	}
	return null;
}

export interface EntradaTraspasoAbiertoEnInicio
	extends Omit<EntradaAccionPendiente, 'traspasoVivo'> {
	transfer: PensionPlanTransfer;
}

/**
 * Si la tarjeta de un traspaso va ABIERTA en Inicio (chirigota#603): solo cuando el traspaso tiene
 * algo pendiente —identidad (también en revisión o rechazada), elegir destino o firmar—, que es
 * justo lo que dice `accionPendienteDelTraspaso`. Sin nada pendiente va plegada, y mientras
 * `/kyc/status` no ha respondido también: «aún no lo sé» no es «hay algo pendiente».
 */
export function traspasoAbiertoEnInicio({
	transfer,
	...resto
}: EntradaTraspasoAbiertoEnInicio): boolean {
	return accionPendienteDelTraspaso({ ...resto, traspasoVivo: transfer }) !== null;
}
